import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Semi-circular gauge component for compliance percentage display
 * Similar to the gauges shown in figure 1 for Thermal Comfort, Lighting, Acoustics, Air Quality
 */
const ComplianceGauge = ({ label, value, size = 120, color = '#888' }) => {
  const radius = (size - 16) / 2;
  const cx = size / 2;
  const cy = size / 2 + 8;
  const startAngle = Math.PI; // 180 degrees (left)
  const endAngle = 0; // 0 degrees (right)
  const clampedValue = Math.min(100, Math.max(0, value));
  const sweepAngle = startAngle - (clampedValue / 100) * Math.PI;

  // Background arc (full semi-circle)
  const bgArcEnd = {
    x: cx + radius * Math.cos(endAngle),
    y: cy - radius * Math.sin(endAngle),
  };
  const bgArcStart = {
    x: cx + radius * Math.cos(startAngle),
    y: cy - radius * Math.sin(startAngle),
  };

  // Value arc
  const valArcEnd = {
    x: cx + radius * Math.cos(sweepAngle),
    y: cy - radius * Math.sin(sweepAngle),
  };

  const bgPath = `M ${bgArcStart.x} ${bgArcStart.y} A ${radius} ${radius} 0 0 1 ${bgArcEnd.x} ${bgArcEnd.y}`;

  const valPath = `M ${bgArcStart.x} ${bgArcStart.y} A ${radius} ${radius} 0 0 1 ${valArcEnd.x} ${valArcEnd.y}`;

  // Color based on value
  const getColor = (val) => {
    if (val >= 80) return '#4CAF50'; // green
    if (val >= 60) return '#8BC34A'; // light green
    if (val >= 40) return '#FFC107'; // yellow
    if (val >= 20) return '#FF9800'; // orange
    return '#F44336'; // red
  };

  const gaugeColor = getColor(clampedValue);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" mx={1}>
      <svg width={size} height={size / 2 + 24} viewBox={`0 0 ${size} ${size / 2 + 24}`}>
        {/* Background arc */}
        <path
          d={bgPath}
          fill="none"
          stroke="#444"
          strokeWidth={8}
          strokeLinecap="round"
        />
        {/* Value arc */}
        {clampedValue > 0 && (
          <path
            d={valPath}
            fill="none"
            stroke={gaugeColor}
            strokeWidth={8}
            strokeLinecap="round"
          />
        )}
        {/* Center text */}
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          fill="#fff"
          fontSize={size * 0.22}
          fontWeight="bold"
        >
          {Math.round(clampedValue)}%
        </text>
      </svg>
      <Typography
        variant="body2"
        sx={{
          color: '#aaa',
          fontSize: '0.72rem',
          textAlign: 'center',
          mt: -0.5,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default ComplianceGauge;
